import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SidebarLayout from '../components/SidebarLayout';
import API from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DollarSign, Plus, CheckCircle2, Send, FileText, Trash2, Download } from 'lucide-react';

function fmtINR(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
}

const STATUS_COLORS = {
  Draft: 'bg-slate-500/10 text-slate-300 border-slate-500/20',
  Approved: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  Paid: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
};

const ROW_STATUS_COLORS = {
  Draft: 'bg-slate-500/10 text-slate-300',
  Paid: 'bg-emerald-500/10 text-emerald-400',
  Skipped: 'bg-rose-500/10 text-rose-400',
};

function currentPeriod() {
  const d = new Date();
  // Default to last month's payroll
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function PayrollPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(currentPeriod());
  const [showPaid, setShowPaid] = useState(null); // {ids:[employee_ids] | null }

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/payroll/batches`, { withCredentials: true });
      setBatches(data);
      if (data.length > 0 && !batchId) setBatchId(data[0].batch_id);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [batchId]);

  const fetchBatch = useCallback(async () => {
    if (!batchId) { setBatch(null); return; }
    try {
      const { data } = await axios.get(`${API}/payroll/batches/${batchId}`, { withCredentials: true });
      setBatch(data);
    } catch (e) { console.error(e); }
  }, [batchId]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);
  useEffect(() => { fetchBatch(); }, [fetchBatch]);

  const generate = async () => {
    try {
      const { data } = await axios.post(`${API}/payroll/batches`, { period }, { withCredentials: true });
      toast.success(`Generated batch ${data.batch_id} for ${data.period}`);
      setBatchId(data.batch_id);
      fetchBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generate failed');
    }
  };

  const updateRow = async (employeeId, payload) => {
    try {
      const { data } = await axios.put(`${API}/payroll/batches/${batchId}/rows/${employeeId}`, payload, { withCredentials: true });
      setBatch(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const approve = async () => {
    try {
      await axios.post(`${API}/payroll/batches/${batchId}/approve`, {}, { withCredentials: true });
      toast.success('Batch approved');
      fetchBatch();
    } catch (e) { toast.error('Approve failed'); }
  };

  const handleMarkPaid = async ({ accountType, paymentMode, paymentDate, employeeIds }) => {
    try {
      const { data } = await axios.post(`${API}/payroll/batches/${batchId}/mark-paid`,
        { account_type: accountType, payment_mode: paymentMode, payment_date: paymentDate, employee_ids: employeeIds },
        { withCredentials: true });
      toast.success(`${data.message} — ${data.finance_entry_ids.length} finance entries created`);
      setShowPaid(null);
      fetchBatch();
      fetchBatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Mark paid failed'); }
  };

  const deleteBatch = async () => {
    if (!batchId) return;
    if (!window.confirm(`Delete payroll batch ${batchId}? Cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/payroll/batches/${batchId}`, { withCredentials: true });
      toast.success('Batch deleted');
      setBatchId(null); fetchBatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/payroll/batches/${batchId}/export`, { withCredentials: true });
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = data.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  if (!isAdmin) {
    return <SidebarLayout title="Payroll"><div className="glass-card p-12 text-center text-slate-400">Admin access required</div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Payroll" subtitle="Monthly salary batches & auto-expense">
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="bg-black/40 border-white/10 text-white h-9 text-xs w-[140px]" data-testid="payroll-period" />
        <Button onClick={generate} className="bg-indigo-600 hover:bg-indigo-700 h-9 text-xs" data-testid="generate-batch-btn">
          <Plus size={14} className="mr-1" /> Generate Batch
        </Button>
        <div className="flex-1" />
        {batches.length > 0 && (
          <Select value={batchId || ''} onValueChange={setBatchId}>
            <SelectTrigger className="w-[260px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="batch-picker">
              <SelectValue placeholder="Select batch" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
              {batches.map(b => (
                <SelectItem key={b.batch_id} value={b.batch_id}>
                  {b.period} · {b.batch_id} · {b.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading && !batch && <div className="glass-card p-12 text-center text-slate-400">Loading...</div>}
      {!loading && !batch && batches.length === 0 && (
        <div className="glass-card p-12 text-center text-slate-500" data-testid="payroll-empty">
          <DollarSign size={32} className="mx-auto mb-3 opacity-30" />
          <div className="text-sm">No payroll batches yet. Pick a period and click "Generate Batch".</div>
        </div>
      )}
      {batch && <BatchView batch={batch} onUpdateRow={updateRow}
        onApprove={approve} onShowPaid={setShowPaid}
        onDelete={deleteBatch} onDownload={downloadCsv} />}

      {showPaid !== null && (
        <MarkPaidDialog
          batch={batch}
          employeeIds={showPaid.ids}
          onClose={() => setShowPaid(null)}
          onConfirm={handleMarkPaid} />
      )}
    </SidebarLayout>
  );
}

function BatchView({ batch, onUpdateRow, onApprove, onShowPaid, onDelete, onDownload }) {
  const unpaidRows = batch.rows.filter(r => r.status !== 'Paid' && r.status !== 'Skipped');
  const paidRows = batch.rows.filter(r => r.status === 'Paid');
  const unpaidNet = unpaidRows.reduce((s, r) => s + (r.net_pay || 0), 0);
  const paidNet = paidRows.reduce((s, r) => s + (r.net_pay || 0), 0);

  return (
    <>
      {/* Batch summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="payroll-summary">
        <SummaryCard label="Period" value={batch.period} />
        <SummaryCard label="Release Date" value={batch.release_date} />
        <SummaryCard label="Working Days" value={batch.working_days} />
        <SummaryCard label="Total Net" value={fmtINR(batch.total_net)} accent="cyan" />
        <SummaryCard label="Status" value={
          <span className={`inline-block text-[10px] px-2 py-1 rounded border ${STATUS_COLORS[batch.status]}`}>{batch.status}</span>
        } />
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {batch.status === 'Draft' && (
          <Button onClick={onApprove} className="bg-amber-600 hover:bg-amber-700 h-9 text-xs" data-testid="approve-batch-btn">
            <Send size={14} className="mr-1" /> Approve Batch
          </Button>
        )}
        {unpaidRows.length > 0 && (
          <Button onClick={() => onShowPaid({ ids: null })} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="mark-all-paid-btn">
            <CheckCircle2 size={14} className="mr-1" /> Mark All Paid ({fmtINR(unpaidNet)})
          </Button>
        )}
        <Button onClick={onDownload} variant="outline" className="border-white/10 text-slate-300 h-9 text-xs" data-testid="export-csv-btn">
          <Download size={14} className="mr-1" /> Export CSV
        </Button>
        {batch.status !== 'Paid' && (
          <Button onClick={onDelete} variant="outline" className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 h-9 text-xs" data-testid="delete-batch-btn">
            <Trash2 size={14} className="mr-1" /> Delete
          </Button>
        )}
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table text-xs" data-testid="payroll-rows">
            <thead><tr>
              <th>Employee</th><th>Dept</th><th className="text-right">Base</th>
              <th className="text-right">Bonus</th><th className="text-right">LOP Days</th>
              <th className="text-right">LOP ₹</th><th className="text-right">Deduction</th>
              <th className="text-right">Net Pay</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {batch.rows.map(r => (
                <PayrollRow key={r.employee_id} row={r} batchStatus={batch.status}
                  onUpdate={(p) => onUpdateRow(r.employee_id, p)}
                  onMarkPaid={() => onShowPaid({ ids: [r.employee_id] })} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {paidNet > 0 && (
        <div className="text-[10px] text-slate-500 mt-2">
          Paid: <span className="text-emerald-400 font-mono">{fmtINR(paidNet)}</span> · Auto-created {paidRows.length} expense entries in Finance.
        </div>
      )}
    </>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="glass-card p-3" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`text-base font-heading font-bold ${accent === 'cyan' ? 'text-cyan-400' : 'text-white'}`}>{value}</div>
    </div>
  );
}

function PayrollRow({ row, batchStatus, onUpdate, onMarkPaid }) {
  const [bonus, setBonus] = useState(row.bonus);
  const [lop, setLop] = useState(row.lop_days);
  const [ded, setDed] = useState(row.manual_deduction);
  const locked = row.status === 'Paid' || row.status === 'Skipped';

  useEffect(() => { setBonus(row.bonus); setLop(row.lop_days); setDed(row.manual_deduction); }, [row]);

  const commit = (field, value) => {
    const num = Number(value) || 0;
    if (field === 'bonus' && num === row.bonus) return;
    if (field === 'lop_days' && num === row.lop_days) return;
    if (field === 'manual_deduction' && num === row.manual_deduction) return;
    onUpdate({ [field]: num });
  };

  return (
    <tr className={locked ? 'opacity-60' : ''} data-testid={`payroll-row-${row.employee_id}`}>
      <td className="text-white font-semibold">{row.employee_name}</td>
      <td className="text-slate-400">{row.department || '—'}</td>
      <td className="text-right font-mono text-slate-300">{fmtINR(row.base_salary)}</td>
      <td className="text-right">
        <Input type="number" value={bonus} onChange={e => setBonus(e.target.value)}
          onBlur={() => commit('bonus', bonus)} disabled={locked}
          className="bg-black/40 border-white/10 text-emerald-400 h-7 text-xs w-[80px] text-right font-mono ml-auto" data-testid={`bonus-${row.employee_id}`} />
      </td>
      <td className="text-right">
        <Input type="number" step="0.5" value={lop} onChange={e => setLop(e.target.value)}
          onBlur={() => commit('lop_days', lop)} disabled={locked}
          className="bg-black/40 border-white/10 text-amber-400 h-7 text-xs w-[60px] text-right font-mono ml-auto" data-testid={`lop-${row.employee_id}`} />
      </td>
      <td className="text-right font-mono text-amber-400">{fmtINR(row.lop_amount)}</td>
      <td className="text-right">
        <Input type="number" value={ded} onChange={e => setDed(e.target.value)}
          onBlur={() => commit('manual_deduction', ded)} disabled={locked}
          className="bg-black/40 border-white/10 text-rose-400 h-7 text-xs w-[80px] text-right font-mono ml-auto" data-testid={`deduction-${row.employee_id}`} />
      </td>
      <td className="text-right font-mono font-bold text-cyan-400">{fmtINR(row.net_pay)}</td>
      <td><span className={`text-[10px] px-2 py-0.5 rounded ${ROW_STATUS_COLORS[row.status] || ROW_STATUS_COLORS.Draft}`}>{row.status || 'Draft'}</span></td>
      <td>
        {!locked && row.net_pay > 0 && (
          <Button onClick={onMarkPaid} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[10px] px-2" data-testid={`pay-${row.employee_id}`}>
            Pay
          </Button>
        )}
        {row.finance_entry_id && <span className="text-[9px] text-emerald-400 font-mono" title="Linked finance entry">{row.finance_entry_id}</span>}
      </td>
    </tr>
  );
}

function MarkPaidDialog({ batch, employeeIds, onClose, onConfirm }) {
  const [accountType, setAccountType] = useState('Company');
  const [paymentMode, setPaymentMode] = useState('Bank Transfer');
  const [paymentDate, setPaymentDate] = useState(batch?.release_date || new Date().toISOString().slice(0, 10));

  const target = employeeIds
    ? batch.rows.filter(r => employeeIds.includes(r.employee_id) && r.status !== 'Paid')
    : batch.rows.filter(r => r.status !== 'Paid' && r.status !== 'Skipped');
  const totalNet = target.reduce((s, r) => s + (r.net_pay || 0), 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f0f15] border-white/10 max-w-md" data-testid="mark-paid-dialog">
        <DialogHeader><DialogTitle className="text-white">Mark {target.length} salary payment{target.length !== 1 ? 's' : ''} as Paid</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded p-3 text-xs text-slate-300">
            This will <span className="text-white font-semibold">create {target.length} finance expense entr{target.length !== 1 ? 'ies' : 'y'}</span> totaling <span className="text-emerald-400 font-mono font-bold">{fmtINR(totalNet)}</span> under category "Salaries".
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Account Type *</label>
              <Select value={accountType} onValueChange={setAccountType}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="paid-account-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[300]">
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="Personal">Personal (paid from founder)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Payment Mode</label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="paid-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[300]">
                  {['UPI', 'Bank Transfer', 'Cash', 'Cheque', 'Card', 'Other'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Payment Date</label>
            <Input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} className="bg-black/40 border-white/10 text-white" data-testid="paid-date" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="ghost" className="text-slate-400" data-testid="paid-cancel">Cancel</Button>
          <Button onClick={() => onConfirm({ accountType, paymentMode, paymentDate, employeeIds })}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="paid-confirm">
            <CheckCircle2 size={14} className="mr-1" /> Confirm & Create Expenses
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
